import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, SelectsOptions, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfLenderFirmDraftDto,
  lenderFirmsGetLenderFirm,
  lenderFirmsCreateChangeRequest,
  lendersGetLender,
  FirmGetAllResponse,
  FirmGetById,
  ComplianceStatus,
} from '@pmp/api';
import { crudToActionMap, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { mapLenderFirmFormValuesToDraft } from '../utils';
import { useFeatureToggle } from '@pmp/hooks';
import { CrudFormProps } from '@pmp/types';

import { FirmsFormSchema } from '.';

export const FirmsCrudForm = memo(
  ({ mode: modeProp, record, valueLists, refreshTable, onClose }: CrudFormProps<FirmGetAllResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<CrudMode | null>(modeProp);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);
    const [defaultComplianceStatus, setDefaultComplianceStatus] = useState<ComplianceStatus | undefined>();

    useEffect(() => {
      lendersGetLender()
        .then(({ data }) => {
          setDefaultComplianceStatus(data?.complianceCheckConfiguration?.defaultFirmStatus ?? undefined);
        })
        .catch(() => {});
    }, []);

    useEffect(() => {
      if (mode === CrudMode.EDIT && !record?.id) {
        notification().error({ message: t('global.errors.unexpected') });
        onClose();
        return;
      }
      setOpen(true);
    }, [mode, record, t, onClose]);

    useEffect(() => {
      if (!open || mode === CrudMode.CREATE || !record?.id) return;
      setLoading(true);
      lenderFirmsGetLenderFirm({ path: { firmId: record.id } })
        .then(({ data }) => {
          ref.current?.form?.setFieldsValue({
            ...data,
            tradingNames:
              data?.tradingNames?.map(tn => ({
                ...tn,
                isActive: (tn as Record<string, unknown>).isActive ?? true,
              })) ?? [],
          });
        })
        .catch(() => {
          notification().error({ message: t('global.errors.unexpected') });
        })
        .finally(() => setLoading(false));
    }, [open, mode, record?.id, t]);

    const mapFormValuesToDraft = useCallback((values: FirmGetById) => mapLenderFirmFormValuesToDraft(values, record), [record]);

    const onConfirm: (formData: FirmGetById) => Promise<ValidationError[] | void> = useCallback(
      (values: FirmGetById) => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const data = mapFormValuesToDraft(values);
        const body: ChangeRequestOfLenderFirmDraftDto = {
          data,
          actionType: crudToActionMap[mode],
        };

        return lenderFirmsCreateChangeRequest({ body }).then(
          ({ data: responseData }: { data: { isValid?: boolean; errors?: Record<string, string[]> } }) => {
            if (!responseData.isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              return transformCrudFormErrors({ ...responseData.errors });
            }
            if (mode === CrudMode.EDIT) refreshTable();
            notification().success({
              message: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes, mapFormValuesToDraft],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.firms.labels.firm') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.firms.labels.firm') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.firms.labels.firm') }),
      };
      return titles[mode];
    }, [mode, t]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const crudProps: CrudProps<FirmGetById> = useMemo(() => {
      return {
        ref,
        open,
        loading,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        mode,
        customForm: true,
        autoFocusFirstInput: true,
        width: 1100,
        wrapperCol: { flex: 'auto' },
        disableDrawerBodyScroll: true,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [open, loading, title, mode, t, handleOnClose, onConfirm, enableFourEyes]);

    return (
      <Crud {...crudProps}>
        <FirmsFormSchema form={ref.current?.form} mode={mode ?? CrudMode.VIEW} selectsOptions={selectsOptions} defaultComplianceStatus={defaultComplianceStatus} entityId={record?.id} />
      </Crud>
    );
  },
);
